// Row-by-row plan comparison shown behind the "compare plans" disclosure.
// Kept next to `pricing.ts` rather than inside it: this is presentation copy
// for one component, while `pricing.ts` mirrors backend billing constants.
//
// Teams are unlimited on every plan. The member row mirrors TIER_MEMBER_LIMITS
// in `apps/autoleads/constants.py`. Keep them in sync: it is enforced.
import { type PricingTier, formatCredits, formatEuros } from "./pricing"
import type { AutoLeadsCatalogTier } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

type Bilingual = { FR: string; EN: string }

// `true` renders a check, `false` a muted dash, a Bilingual renders as text.
export type ComparisonCell = boolean | Bilingual

export type ComparisonRow = {
  key: string
  label: Bilingual
  // Static per-tier answers, or a value computed from the tier so the figures
  // can never drift from TIERS.
  values?: Record<AutoLeadsCatalogTier, ComparisonCell>
  derive?: (tier: PricingTier) => string
}

export type ComparisonGroup = {
  key: string
  title: Bilingual
  rows: ComparisonRow[]
}

const UNLIMITED: Bilingual = { FR: "Illimité", EN: "Unlimited" }
const SOON: Bilingual = { FR: "Bientôt", EN: "Coming soon" }
const ONE: Bilingual = { FR: "1", EN: "1" }
const FIVE: Bilingual = { FR: "5", EN: "5" }

export const COMPARISON_GROUPS: ComparisonGroup[] = [
  {
    key: "volume",
    title: { FR: "Volume", EN: "Volume" },
    rows: [
      {
        key: "price",
        label: { FR: "Prix par mois", EN: "Price per month" },
        derive: (tier) => formatEuros(tier.priceEur, 0),
      },
      {
        key: "credits",
        label: { FR: "Crédits inclus", EN: "Credits included" },
        derive: (tier) => formatCredits(tier.credits),
      },
    ],
  },
  {
    key: "team",
    title: { FR: "Équipe", EN: "Team" },
    rows: [
      {
        key: "teams",
        label: { FR: "Équipes", EN: "Teams" },
        values: { starter: UNLIMITED, growth: UNLIMITED, scale: UNLIMITED },
      },
      {
        key: "members",
        label: { FR: "Membres par équipe", EN: "Members per team" },
        values: { starter: ONE, growth: FIVE, scale: UNLIMITED },
      },
    ],
  },
  {
    key: "features",
    title: { FR: "Fonctionnalités", EN: "Features" },
    rows: [
      {
        key: "filters",
        label: { FR: "Filtres clés", EN: "Key filters" },
        values: { starter: true, growth: true, scale: true },
      },
      {
        key: "enrichment",
        label: { FR: "Enrichissement", EN: "Enrichment" },
        values: { starter: true, growth: true, scale: true },
      },
      {
        key: "sequences",
        label: { FR: "Séquences email", EN: "Email sequences" },
        values: { starter: true, growth: true, scale: true },
      },
      {
        key: "api",
        label: { FR: "API", EN: "API" },
        values: { starter: false, growth: SOON, scale: SOON },
      },
      {
        key: "white_label",
        label: { FR: "Vente en marque blanche", EN: "White-label reselling" },
        values: { starter: false, growth: false, scale: true },
      },
    ],
  },
  {
    key: "support",
    title: { FR: "Accompagnement", EN: "Support" },
    rows: [
      {
        key: "support_level",
        label: { FR: "Support", EN: "Support" },
        values: {
          starter: { FR: "Email", EN: "Email" },
          growth: { FR: "Prioritaire", EN: "Priority" },
          scale: { FR: "Prioritaire", EN: "Priority" },
        },
      },
      {
        key: "onboarding",
        label: { FR: "Mise en place", EN: "Guided setup" },
        values: { starter: true, growth: true, scale: true },
      },
      {
        key: "monthly",
        label: { FR: "Point mensuel", EN: "Monthly check-in" },
        values: { starter: false, growth: false, scale: true },
      },
    ],
  },
]
